import {
  useCreateOrderShift,
  useOrderCapacityMode,
  useOrderShifts,
  useSetOrderCapacityMode,
  useUpdateOrderShift,
} from '../hooks/useOrderSetup';
import { LeadTimeDiagram } from '../components/LeadTimeDiagram';
import { useDeliveryLeg } from '../hooks/useDeliveryLeg';
import { useOpeningMinutes } from '../hooks/useOpeningMinutes';
import type { OrderShift, OrderShiftRules } from '@restaurant/api-client';
import { OPENING_BUFFER_MINUTES_MAX } from '@restaurant/api-contracts';
import { DEFAULT_PREP_MINUTES } from '@/features/orders/utils/time';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  Segmented,
  f7,
} from 'framework7-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';

type OrderType = 'PICKUP' | 'DELIVERY';
type ShiftMode = 'SIMPLE' | 'ADVANCED';

/** Parse "" → null, "12" → 12, anything else → NaN. */
function parseCap(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

/** Parse "" → null, "0"+ → n, anything else → NaN (for prep / notice). */
function parseLead(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
}

type OrderCapacityT = ReturnType<typeof useT>['orderCapacity'];
type WindowShift = Extract<OrderShift, { scope: 'WINDOW' }>;

/** "Mon, Wed, Fri" — or "Every day" when all seven are on. */
function describeWindowDays(days: number[], oc: OrderCapacityT): string {
  if (days.length >= 7) return oc.everyDay;
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => oc.dayNamesShort[d] ?? '')
    .join(', ');
}

/** Per-slot cap summary: "200 orders / 15 min" (ADVANCED), "200 orders/hour"
 *  (SIMPLE), or "no cap" when the window inherits an unlimited default. */
function describeWindowRules(rules: OrderShiftRules, oc: OrderCapacityT): string {
  if (rules.maxOrdersPerSlot == null) return oc.noCap;
  return rules.mode === 'ADVANCED'
    ? oc.ordersPerSlotMin(rules.maxOrdersPerSlot, rules.slotMinutes)
    : oc.ordersPerHour(rules.maxOrdersPerSlot);
}

// ── Inline default (BASELINE) editor ──────────────────────────────────────────

type Draft = {
  maxOrders: string;
  slotMinutes: string;
  // '' = the cap window equals the slot interval (cap per booking slot).
  capacityWindow: string;
  prepTimeMinutes: string;
  schedulingLeadMinutes: string;
  openingBufferMinutes: string;
};

function draftOf(baseline: OrderShift | undefined): Draft {
  const rules = baseline?.rules;
  return {
    maxOrders: rules?.maxOrdersPerSlot != null ? String(rules.maxOrdersPerSlot) : '',
    // A NEW channel defaults to a 30-min booking grid (guests expect 18:00,
    // 18:30, …) capped over a 60-min window (owners think "N orders per hour").
    // A legacy SIMPLE baseline (always 60-min) keeps its own value until the
    // operator edits + saves it (which migrates to ADVANCED).
    slotMinutes: rules?.mode === 'ADVANCED' ? String(rules.slotMinutes) : '30',
    // Only pre-fill the hourly default when there's nothing configured yet — an
    // existing baseline keeps its stored value (blank = window equals the slot),
    // so opening the screen never silently rewrites a live channel's capacity.
    capacityWindow:
      rules?.mode === 'ADVANCED'
        ? rules.capacityWindowMinutes != null
          ? String(rules.capacityWindowMinutes)
          : ''
        : '60',
    prepTimeMinutes: baseline?.prepTimeMinutes != null ? String(baseline.prepTimeMinutes) : '',
    schedulingLeadMinutes:
      baseline?.schedulingLeadMinutes != null ? String(baseline.schedulingLeadMinutes) : '',
    openingBufferMinutes:
      baseline?.openingBufferMinutes != null ? String(baseline.openingBufferMinutes) : '',
  };
}

// The capacity-mode selector is hidden for now - every channel runs on the
// ADVANCED rule shape (custom slot length + per-slot cap). SIMPLE is the
// legacy mode; an existing SIMPLE channel is migrated to ADVANCED the first
// time its default is saved (see `save` below).
// Lets the editor surface its dirty/saving state and save action up to the
// page navbar, which hosts the Save button (iOS top-right convention).
type SaveBinding = { dirty: boolean; saving: boolean; save: () => void };

function DefaultEditor({
  orderType,
  baseline,
  currentMode,
  onSaveBind,
}: {
  orderType: OrderType;
  baseline: OrderShift | undefined;
  // The channel's currently-stored capacity mode, so we only flip it when needed.
  currentMode: ShiftMode | undefined;
  onSaveBind: (b: SaveBinding) => void;
}) {
  const t = useT();
  const create = useCreateOrderShift();
  const update = useUpdateOrderShift();
  const setCapacityMode = useSetOrderCapacityMode();
  // Delivery's preview must show the drive too — "ready" is when the food leaves
  // the kitchen, not when the guest has it.
  const deliveryLeg = useDeliveryLeg(orderType);
  const openingMinutes = useOpeningMinutes(orderType);
  const [draft, setDraft] = useState<Draft>(() => draftOf(baseline));

  // Re-sync if the row changes underneath us (channel switch / refetch).
  useEffect(() => setDraft(draftOf(baseline)), [baseline]);

  const saving = create.isPending || update.isPending;
  // Light the Save button only once an edit diverges from the saved default;
  // a successful save refetches the baseline, which re-syncs the draft and dims
  // it again. Draft is a flat string map, so a JSON compare is exact.
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(baseline));

  // After the default is persisted, make sure the channel is on ADVANCED so the
  // resolver actually uses the slot length we just saved. No-op when already there.
  const ensureAdvancedMode = () => {
    if (currentMode !== 'ADVANCED') {
      setCapacityMode.mutate({ orderType, shiftMode: 'ADVANCED' });
    }
  };

  const save = () => {
    const maxOrders = parseCap(draft.maxOrders);
    if (Number.isNaN(maxOrders)) {
      setDraft(draftOf(baseline));
      return f7.dialog.alert(t.orderCapacity.capAlert);
    }
    const slot = parseCap(draft.slotMinutes);
    if (slot == null || Number.isNaN(slot)) {
      setDraft(draftOf(baseline));
      return f7.dialog.alert(t.orderCapacity.slotAlert);
    }
    // Capacity window: blank = same as the slot (cap per booking slot). A value
    // must be a positive whole multiple of the slot so buckets align to the grid.
    const window = parseCap(draft.capacityWindow);
    if (
      Number.isNaN(window) ||
      (typeof window === 'number' && (window < (slot as number) || window % (slot as number) !== 0))
    ) {
      setDraft(draftOf(baseline));
      return f7.dialog.alert(t.orderCapacity.capacityWindowAlert);
    }
    const rules: OrderShiftRules = {
      mode: 'ADVANCED',
      slotMinutes: slot as number,
      maxOrdersPerSlot: maxOrders as number | null,
      ...(typeof window === 'number' ? { capacityWindowMinutes: window } : {}),
    };
    const prep = parseLead(draft.prepTimeMinutes);
    const lead = parseLead(draft.schedulingLeadMinutes);
    if (Number.isNaN(prep) || Number.isNaN(lead)) {
      setDraft(draftOf(baseline));
      return f7.dialog.alert(t.orderCapacity.prepNoticeAlert);
    }
    const buffer = parseLead(draft.openingBufferMinutes);
    if (Number.isNaN(buffer) || (buffer ?? 0) > OPENING_BUFFER_MINUTES_MAX) {
      setDraft(draftOf(baseline));
      return f7.dialog.alert(t.orderCapacity.openingBufferAlert(OPENING_BUFFER_MINUTES_MAX));
    }

    const onError = (e: unknown) => {
      const msg = e instanceof Error ? e.message.trim() : '';
      // The API maps a baseline conflict to a bare "409" - translate it.
      f7.dialog.alert(
        !msg || /^\d{3}$/.test(msg) ? t.orderCapacity.saveErrorGeneric : msg,
        t.orderCapacity.saveFailedTitle,
      );
    };

    // Rule mode is immutable: only an existing ADVANCED baseline can be edited
    // in place. A legacy SIMPLE baseline is left to hibernate while we create
    // the ADVANCED one alongside it, then flip the channel over.
    if (baseline && baseline.rules.mode === 'ADVANCED') {
      update.mutate(
        {
          shiftId: baseline.id,
          data: {
            rules,
            prepTimeMinutes: prep as number | null,
            schedulingLeadMinutes: lead as number | null,
            openingBufferMinutes: buffer,
          },
        },
        { onError, onSuccess: ensureAdvancedMode },
      );
    } else {
      create.mutate(
        {
          scope: 'BASELINE',
          orderType,
          label: 'Default',
          rules,
          prepTimeMinutes: prep as number | null,
          schedulingLeadMinutes: lead as number | null,
          openingBufferMinutes: buffer,
        },
        { onError, onSuccess: ensureAdvancedMode },
      );
    }
  };

  // Report state to the page navbar every render so its Save link reflects the
  // current dirty/saving flags and always invokes the latest `save` closure
  // (which captures the up-to-date draft).
  useEffect(() => {
    onSaveBind({ dirty, saving, save });
  });

  return (
    <>
      <BlockTitle>{t.orderCapacity.defaultCapacity}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.orderCapacity.defaultCapacityFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={t.orderCapacity.slotIntervalLabel}
          type="number"
          placeholder="30"
          value={draft.slotMinutes}
          onInput={(e) =>
            setDraft((d) => ({ ...d, slotMinutes: (e.target as HTMLInputElement).value }))
          }
        />
        <ListInput
          label={t.orderCapacity.ordersPerSlotLabel}
          type="number"
          placeholder={t.orderCapacity.noCap}
          value={draft.maxOrders}
          onInput={(e) =>
            setDraft((d) => ({ ...d, maxOrders: (e.target as HTMLInputElement).value }))
          }
        />
        <ListInput
          label={t.orderCapacity.capacityWindowLabel}
          type="number"
          // Blank reads as "= the slot interval": the cap applies per booking slot.
          placeholder={draft.slotMinutes || '30'}
          value={draft.capacityWindow}
          onInput={(e) =>
            setDraft((d) => ({ ...d, capacityWindow: (e.target as HTMLInputElement).value }))
          }
        />
        <ListInput
          label={t.orderCapacity.prepTimeLabel}
          type="number"
          placeholder={String(DEFAULT_PREP_MINUTES)}
          value={draft.prepTimeMinutes}
          onInput={(e) =>
            setDraft((d) => ({ ...d, prepTimeMinutes: (e.target as HTMLInputElement).value }))
          }
        />
        <ListInput
          label={t.orderCapacity.minNoticeLabel}
          type="number"
          placeholder={t.orderCapacity.nonePlaceholder}
          value={draft.schedulingLeadMinutes}
          onInput={(e) =>
            setDraft((d) => ({
              ...d,
              schedulingLeadMinutes: (e.target as HTMLInputElement).value,
            }))
          }
        />
        <ListInput
          label={t.orderCapacity.openingBufferLabel}
          type="number"
          placeholder={t.orderCapacity.nonePlaceholder}
          value={draft.openingBufferMinutes}
          onInput={(e) =>
            setDraft((d) => ({
              ...d,
              openingBufferMinutes: (e.target as HTMLInputElement).value,
            }))
          }
        />
      </List>
      <BlockFooter style={{ marginTop: -6 }}>{t.orderCapacity.capacityWindowFooter}</BlockFooter>
      <BlockTitle>{t.orderCapacity.preview}</BlockTitle>
      {(() => {
        const p = parseLead(draft.prepTimeMinutes);
        const n = parseLead(draft.schedulingLeadMinutes);
        const b = parseLead(draft.openingBufferMinutes);
        const prepMinutes = typeof p === 'number' ? p : DEFAULT_PREP_MINUTES;
        const noticeMinutes = typeof n === 'number' ? n : 0;
        const openingBufferMinutes = typeof b === 'number' ? b : 0;
        return (
          <LeadTimeDiagram
            prepMinutes={prepMinutes}
            noticeMinutes={noticeMinutes}
            openingBufferMinutes={openingBufferMinutes}
            openingMinutes={openingMinutes}
            delivery={deliveryLeg}
          />
        );
      })()}
    </>
  );
}

// ── Per-channel body ──────────────────────────────────────────────────────────

function ChannelBody({
  orderType,
  onSaveBind,
}: {
  orderType: OrderType;
  onSaveBind: (b: SaveBinding) => void;
}) {
  const t = useT();
  const { data: capacity } = useOrderCapacityMode(orderType);
  // Read every shift (not just the active mode's): the channel always edits the
  // ADVANCED baseline, which may be hibernating while the stored mode is still
  // SIMPLE. Filtering by active mode here would hide it and make the editor
  // re-CREATE a baseline that already exists → 409 conflict.
  const { data: shifts, isLoading, error } = useOrderShifts(orderType, false);

  const live = shifts ?? [];
  const baseline = live.find((s) => s.scope === 'BASELINE' && s.rules.mode === 'ADVANCED');
  // Time-specific overrides — different capacity/hours for e.g. lunch vs dinner.
  // Includes inactive ones so the operator can re-enable them.
  const windows = live.filter((s): s is WindowShift => s.scope === 'WINDOW');

  if (isLoading) {
    return (
      <Block className="text-align-center" style={{ paddingTop: 40 }}>
        <Preloader />
      </Block>
    );
  }

  return (
    <>
      {capacity ? (
        <DefaultEditor
          orderType={orderType}
          baseline={baseline}
          currentMode={capacity.shiftMode}
          onSaveBind={onSaveBind}
        />
      ) : (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          <Preloader />
        </Block>
      )}

      {/* Special hours (WINDOW shifts): per-time-window capacity that overrides the
          default on the days/hours it covers. Tap to edit; "+" to add. */}
      <BlockTitle
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>{t.orderCapacity.specialHours}</span>
        <F7Link href={`/settings/orders/shifts/new/${orderType}`} iconF7="plus" />
      </BlockTitle>
      {windows.length === 0 ? (
        <BlockFooter style={{ marginTop: -6 }}>{t.orderCapacity.noSpecialHours}</BlockFooter>
      ) : (
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
          {windows.map((s) => (
            <ListItem
              key={s.id}
              link={`/settings/orders/shifts/${s.id}`}
              animate
              title={s.isActive ? s.label : t.orderCapacity.offSuffix(s.label)}
              subtitle={`${s.startTime}–${s.endTime} · ${describeWindowDays(s.dayOfWeek, t.orderCapacity)}`}
              text={describeWindowRules(s.rules, t.orderCapacity)}
            >
              <i slot="media" className="f7-icons">
                clock
              </i>
            </ListItem>
          ))}
        </List>
      )}

      {error && <Block className="text-color-red">{(error as Error).message}</Block>}
    </>
  );
}

export default function OrderShiftsPage() {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const [tab, setTab] = useState<OrderType>('PICKUP');

  // The Save button lives in the navbar (iOS top-right), but the editable state
  // lives in the active tab's DefaultEditor. It binds its dirty/saving flags +
  // save action up here; the ref always holds the latest closure so a tap saves
  // the current draft.
  const saveRef = useRef<() => void>(() => {});
  const [saveUI, setSaveUI] = useState<{ dirty: boolean; saving: boolean }>({
    dirty: false,
    saving: false,
  });
  const onSaveBind = useCallback((b: SaveBinding) => {
    saveRef.current = b.save;
    setSaveUI((prev) =>
      prev.dirty === b.dirty && prev.saving === b.saving
        ? prev
        : { dirty: b.dirty, saving: b.saving },
    );
  }, []);
  // Switching channels mounts a fresh editor; reset until it re-binds.
  useEffect(() => setSaveUI({ dirty: false, saving: false }), [tab]);

  const canSave = saveUI.dirty && !saveUI.saving;

  // Switching channels mounts a fresh editor and drops the current draft, so
  // guard the switch when the active tab has unsaved edits - otherwise a tap on
  // the other channel silently discards them.
  const switchTab = (next: OrderType) => {
    if (next === tab) return;
    if (saveUI.dirty) {
      f7.dialog.confirm(t.orderCapacity.unsavedConfirm, t.orderCapacity.unsavedTitle, () =>
        setTab(next),
      );
      return;
    }
    setTab(next);
  };

  return (
    <Page name="order-shifts">
      <Navbar title={t.settingsFeature.orderShifts} backLink={settingsBackLink}>
        <NavRight>
          <F7Link
            onClick={canSave ? () => saveRef.current() : undefined}
            style={canSave ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          >
            {saveUI.saving ? t.orderCapacity.saving : t.orderCapacity.save}
          </F7Link>
        </NavRight>
      </Navbar>

      <Block style={{ marginTop: 16, marginBottom: 0 }}>
        <Segmented strong>
          <Button active={tab === 'PICKUP'} onClick={() => switchTab('PICKUP')}>
            {t.orderCapacity.pickup}
          </Button>
          <Button active={tab === 'DELIVERY'} onClick={() => switchTab('DELIVERY')}>
            {t.orderCapacity.delivery}
          </Button>
        </Segmented>
      </Block>

      <ChannelBody key={tab} orderType={tab} onSaveBind={onSaveBind} />
    </Page>
  );
}
